import { PeoplePicker, PersonType } from "@microsoft/mgt-react";
import { isEqual } from "lodash";
import { Dispatch, FC, Fragment, useCallback, useMemo } from "react";
import { ExtraStateContext } from "..";
import { approverType } from "../../../../../../constants/itCodeRequestConstants";
import { useSelector } from "../../../../../../store/store";
import InformationList, {
  ColumnContent,
  LeftRightContent,
  ListContent,
} from "../../../../components/InformationList";
import TableModal from "../../../../components/detail";
import { useProcess } from "../../../../process-context";
import { FileAttachmentCard } from "../../../../upload";
import CommentSection from "../../../commentsection/comment-sections";
import { tableColumns } from "./table-column";

interface Props {
  setEmployeeName: Dispatch<any>;
}

const FormContent: FC<Props> = (props) => {
  const { setEmployeeName } = props;

  const { Request, approvalStep, approvalType, comment, setComment, setEmployeesSelected } =
    useProcess<ExtraStateContext>();

  const { fields } = Request;

  /**
   * Selector
   */
  const user = useSelector((state) => state.user);

  const {
    eDeptHeadComment,
    eITLeadComment,
    eAssigneeComment,
    eApprovedBy,
    eApprovedByITLead,
    eAssignee,
  } = fields;

  const onEmployeeChange = useCallback(
    (selectedOptions: any) => {
      setEmployeesSelected?.(
        selectedOptions &&
          selectedOptions?.target?.selectedPeople[0]?.userPrincipalName.toLowerCase()
      );
      setEmployeeName(
        selectedOptions ? selectedOptions?.target?.selectedPeople[0]?.displayName : null
      );
    },
    [setEmployeesSelected, setEmployeeName]
  );

  const comments = useMemo(() => {
    return [
      {
        hidden: !eDeptHeadComment,
        content: `Comment của ${eApprovedBy.split("|")[1]}: ${eDeptHeadComment}`,
      },
      {
        hidden: !eITLeadComment,
        content: `Comment của ${eApprovedByITLead.split("|")[1]}: ${eITLeadComment}`,
      },
      {
        hidden: !eAssigneeComment,
        content: `Comment của ${eAssignee.split("|")[1]}: ${eAssigneeComment}`,
      },
      {
        hidden: approvalStep !== approvalType,
        content: (
          <div className="input">
            <textarea value={comment} onChange={(e) => setComment(e.target.value)} />
          </div>
        ),
      },
    ];
  }, [
    eDeptHeadComment,
    eApprovedBy,
    eITLeadComment,
    eApprovedByITLead,
    eAssigneeComment,
    eAssignee,
    approvalStep,
    approvalType,
    comment,
    setComment,
  ]);

  const smColData = useMemo(() => {
    return [
      {
        content: (
          <LeftRightContent
            left="Người yêu cầu:"
            right={<strong>{Request.createdBy.user.displayName}</strong>}
          />
        ),
      },
      {
        content: (
          <LeftRightContent
            left="SĐT Người yêu cầu:"
            right={<strong>{Request.fields.ePhoneNumber}</strong>}
            smColumn={false}
          />
        ),
      },
      {
        content: (
          <LeftRightContent
            left="Cơ quan/Đơn vị:"
            right={<strong>{Request.fields.eDepartment}</strong>}
          />
        ),
      },
      {
        hidden: {
          sm: isEqual(Request.fields.eDepartment, Request.fields.eUnit),
        },
        content: (
          <LeftRightContent
            left="Đơn vị:"
            right={<strong>{Request.fields.eUnit}</strong>}
          />
        ),
      },
      {
        hidden: {
          sm: isEqual(Request.fields.eUnit, Request.fields.eUnion),
        },
        content: (
          <LeftRightContent
            left="Đội/Tổ:"
            right={<strong>{Request.fields.eUnion}</strong>}
          />
        ),
      },
      {
        content: <LeftRightContent left="Mô tả:" right={Request.fields.eDescription} />,
      },
    ];
  }, [Request]);

  const fullColData = useMemo(() => {
    return [
      {
        content: <TableModal data={JSON.parse(Request.fields.eContent)} columns={tableColumns} />,
      },
      {
        content: (
          <Fragment>
            Tệp đính kèm
            <div className="mt-2 d-flex flex-column">
              <FileAttachmentCard
                listFile={
                  Request.fields.eFileContent ? JSON.parse(Request.fields.eFileContent) : []
                }
              />
            </div>
          </Fragment>
        ),
      },
      {
        content: (
          <ListContent
            label="Comments"
            data={comments}
            getItemHidden={(item) => item.hidden}
            renderItemContent={(item) => item.content}
          />
        ),
      },
      {
        content: (
          <Fragment>
            Thông tin trao đổi
            <div className="mt-2">
              <CommentSection comments={[]} data={Request} user={user} id={5} className="mb-4" />
            </div>
          </Fragment>
        ),
      },
      {
        hidden: approvalType !== approverType.itlead || approvalStep !== approvalType,
        content: (
          <ColumnContent
            label="Cán bộ/Chuyên viên CNTT thực hiện"
            content={
              <div className="people_picker-custom-detail">
                <PeoplePicker
                  className="people-picker"
                  placeholder="Vui lòng chọn cán bộ/ Chuyên viên CNTT thực hiện"
                  type={PersonType.any}
                  selectionChanged={onEmployeeChange}
                />
              </div>
            }
          />
        ),
      },
    ];
  }, [Request, comments, user, approvalType, approvalStep, onEmployeeChange]);

  return (
    <Fragment>
      <InformationList
        size="sm-col"
        data={smColData}
        renderContent={(item) => item.content}
        getItemHidden={(item) => item.hidden}
      />
      <InformationList
        size="full-col"
        data={fullColData}
        getItemHidden={(item) => item.hidden}
        renderContent={(item) => item.content}
      />
    </Fragment>
  );
};

export default FormContent;
